import React from 'react';
import EarmarkIcons from '../../../EarmarkIcons/EarmarkIcons';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { PDFSafeTableHeaderCaption } from '../../../CommonComponents/OPL/HtmlTable/components/PDFSafeTableHeaderCaption/PDFSafeTableHeaderCaption';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../../../CommonComponents/OPL/Text/TruncatedText';
import { Tooltip } from '../../../CommonComponents/OPL/Tooltip/Tooltip';
import { formatCurrency } from '../../../CommonUtils/formatNumber';
import {
  AccountNameButton,
  NameCellContent,
  NetWorthAccountsHtmlTable,
} from './NetWorthAccountsTable.styles';
import {
  AccountGroup,
  AccountItem,
  isBalanceColumn,
  navigateToAccount,
} from './NetWorthAccountsTable.meta';

interface Props {
  accountGroups: AccountGroup[];
  descriptors: string[];
  clientId: string;
  hasAccessToLinkAccounts: boolean;
}

const AccountNameText = ({ name, isLinkable }: { name: string; isLinkable: boolean }) => (
  <Tooltip content={name} placement="top" delay={[400, 0]}>
    <Text
      isTruncated
      color={isLinkable ? 'primary.500' : 'neutral.700'}
      fontWeight={isLinkable ? 500 : 400}
      width="100%"
      minWidth={0}
      textAlign="left"
    >
      {name}
    </Text>
  </Tooltip>
);

const renderAccountName = (
  item: AccountItem,
  clientId: string,
  hasAccessToLinkAccounts: boolean,
) => {
  const isLinkable = item.LinkToTransactions && hasAccessToLinkAccounts;
  const name = <AccountNameText name={item.Name} isLinkable={isLinkable} />;

  if (!isLinkable) {
    return <NameCellContent>{name}</NameCellContent>;
  }

  return (
    <AccountNameButton type="button" onClick={() => navigateToAccount(item, clientId)}>
      {name}
    </AccountNameButton>
  );
};

const renderCell = (
  descriptor: string,
  item: AccountItem,
  clientId: string,
  hasAccessToLinkAccounts: boolean,
) => {
  switch (descriptor) {
    case 'Name':
      return renderAccountName(item, clientId, hasAccessToLinkAccounts);
    case 'Institution':
      return (
        <NameCellContent>
          <TruncatedText width="100%" minWidth={0}>
            {item.Institution}
          </TruncatedText>
        </NameCellContent>
      );
    case 'Goals':
      return <EarmarkIcons earmarks={item.Earmarks} />;
    case 'Account Type':
      return <Text>{item.Type}</Text>;
    case 'Balance':
      return (
        <Text
          variant="tabular"
          textAlign="right"
          color={item.Value < 0 ? 'orange.500' : 'neutral.700'}
        >
          {item.Value < 0 ? formatCurrency(Math.abs(item.Value)) : formatCurrency(item.Value)}
        </Text>
      );
    default:
      return null;
  }
};

export const NetWorthAccountsTable = ({
  accountGroups,
  descriptors,
  clientId,
  hasAccessToLinkAccounts,
}: Props) => {
  if (!accountGroups.length) {
    return null;
  }

  return (
    <Box>
      {accountGroups.map(accountGroup => (
        <NetWorthAccountsHtmlTable
          key={accountGroup.Name}
          variant="embedded"
          columnSpacing="small"
          mb="16"
        >
          <table>
            <colgroup>
              <col width="25%" />
              <col width="25%" />
              <col width="15%" />
              <col width="20%" />
              <col width="15%" />
            </colgroup>

            <thead>
              <PDFSafeTableHeaderCaption colSpan={descriptors.length}>
                {accountGroup.Name}
              </PDFSafeTableHeaderCaption>

              <tr>
                {descriptors.map(descriptor => (
                  <th key={descriptor} scope="col">
                    <Text
                      variant="tableHeader"
                      textAlign={isBalanceColumn(descriptor) ? 'right' : 'left'}
                    >
                      {descriptor}
                    </Text>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {accountGroup.Items.map(item => (
                <tr key={item.Id}>
                  {descriptors.map(descriptor => (
                    <td key={descriptor}>
                      {renderCell(descriptor, item, clientId, hasAccessToLinkAccounts)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </NetWorthAccountsHtmlTable>
      ))}
    </Box>
  );
};
